import { useState } from "react";
import { faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import { formatDate } from "../../utils/formatting";
import { getUserResellerType, ResellerType } from "../../utils/getUserInformation";
import useFetchAuto from "../../hooks/useFetchAuto";
import useLocalStorage from "../../hooks/useLocalStorage";
import { useSession, LogoutReason } from "../Auth/hooks/useSession";
import AuthLogoutMessage from "../Auth/components/AuthLogoutMessage";
import LoadingSpinner from "../../components/loadingSpinner/LoadingSpinner";
import DataTable from "../../components/DataTable";
import Button, { ButtonType } from "../../components/Button";
import AddReseller from "./AddReseller";
import ViewReseller from "./ViewReseller";
import EditReseller from "./EditReseller";
import CopyToClipboard from "../../components/copyToClipboard/CopyToClipboard";
import InfoIcon from "../../components/infoIcon/InfoIcon";

const ListResellers = () => {
	const { reason } = useSession();
	const [resellerType, setResellerType] = useState(ResellerType.Invalid)

	useLocalStorage("ap-liu", () => {
		setResellerType(getUserResellerType());
	});
	const [showAddModal, setAddModal] = useState(false);
	const [showViewModal, setViewModal] = useState(false);
	const [editing, setEditing] = useState(false);
	const [viewItemData, setViewItemData] = useState({});
	const [packageData, setPackageData] = useState(false);
	const [fetch, setFetch] = useState(true);
	const { loading, data } = useFetchAuto("/api/Resellers/", null, fetch);

	const onClickReseller = (adding, item) => {
		if (adding) {
			setAddModal(true);
		}
		else {
			setEditing(false);
			setViewItemData(item.data);
			setViewModal(true);
		}
	};

	const columns = [
		{
			field: "Name",
			headerName: "Name",
			class: "col-wide",
			valueGetter: (data) => (data.Name.length) ? data.Name : <em className="grey">Invite pending:&nbsp;{data.PrimaryContactEmail}</em>
		},
		{
			field: "Reference",
			headerName: (
				<div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
					<span>Reseller Reference</span>
					<InfoIcon text="The reseller reference is used by the order API to assign customers to the correct reseller." />
				</div>
			),
			class: "col-medium",
			valueGetter: (data) => {
				if (!data.Reference || data.Reference.trim() === "") {
					return <em className = "grey" >Reseller login required</em >;
				}

				return (
					<div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
						<span>{data.Reference}</span>
						<CopyToClipboard text={data.Reference} />
					</div>
				);
			}
		},
		{
			field: "Customers",
			headerName: "Customers",
			class: "col-narrow",
			valueGetter: (data) => data.Customers !== -1 ? data.Customers : "-"
		},
		{
			field: "SubResellers",
			headerName: "Sub-Resellers",
			class: "col-narrow",
			valueGetter: (data) => data.SubResellers !== -1 ? data.SubResellers : "-"
		},
		{
			field: "CreationDate",
			headerName: "Date Created",
			class: "col-medium",
			valueGetter: (data) => formatDate(data.CreationDate)
		}
	];

	return (
		<div>
			{(reason !== LogoutReason.None)
				? <AuthLogoutMessage reason={reason} />
				: (<>
					<h1>{resellerType === ResellerType.iTEL ? "My Billable Resellers" : "My Resellers"}</h1>
					<p className="mb-6">
						{resellerType === ResellerType.iTEL || resellerType === ResellerType.Billable
							? "Set up your resellers on the ucSKILLS Admin Portal. This will allow you to provision customers for them via the API, let them set their white labelling for ucSKILLS, and allow them to see their customers."
							: "Set up your resellers on the ucSKILLS Admin Portal. This will allow them to set their white labelling for ucSKILLS and allow them to see their customers."}
					</p>
					{loading
						? <LoadingSpinner active={loading} />
						: (<DataTable columns={columns} rows={data} columnIdName="Id" onRowClick={(params) => onClickReseller(false, params)} />)
					}
					<Button type={ButtonType.Secondary} icon={faCirclePlus} label={"Add New Reseller"} onClick={() => onClickReseller(true)} />
					{showAddModal && <AddReseller showModal={showAddModal} setModal={setAddModal} setFetchParentData={setFetch} master={resellerType === ResellerType.iTEL} />}
					{showViewModal &&
						(editing ?
							<EditReseller
								open={editing}
								setOpenEditor={setEditing}
								showModal={showViewModal}
								setModal={setViewModal}
								master={resellerType === ResellerType.iTEL}
								resellerId={viewItemData.Id}
								items={packageData}
								setItem={setViewItemData}
							/> :
							<ViewReseller
								open={!editing}
								setOpenEditor={setEditing}
								showModal={showViewModal}
								setModal={setViewModal}
								master={resellerType === ResellerType.iTEL}
								item={viewItemData}
								setFetchParentData={setFetch}
								setPackageData={setPackageData}
							/>
						)
					}
				</>)
			}
		</div>
	);
};

export default ListResellers;